import DataTransferService from '@/services/dataTransferService'
const dataTransferService = DataTransferService()

// import { useApplicationStore } from '@/stores/applicationStore'
import { reactive, ref, computed } from 'vue'
import { copyObject } from '@/helpers/utilsHelper'
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'

import useLogger from '@/services/loggerService'
import { useUserStore } from '@/stores/userStore'

import { CONFLICT_POLICY_TO_CLONING_PRESET } from '@/config/presetsConfig'

export default function usePresets(instanceName, urlHelper, basePresets = []) {
	const { t: $t } = useI18n()
	const loadingState = reactive({
		presetsRequest: false,
		presetsEditing: false,
		presetsDeleting: false,
		presetsCreating: false,
		presetsRestoring: false,
		presetsChangingOrder: false,
	})
	const presets = ref([])
	const selectedPresetId = ref(null)
	const selectedPreset = computed(
		() => selectedPresetId.value && presets.value.find(({ id }) => id === selectedPresetId.value)
	)

	const logger = useLogger()
	const userStore = useUserStore()
	const { selectedCompanyId } = storeToRefs(userStore)

	const sortPresetsByOrder = (presets) => presets.sort((a, b) => a.order - b.order)

	const getPresets = async (presetCode = null) => {
		loadingState.presetsRequest = true

		try {
			const request = await dataTransferService.presets.getPresets(selectedCompanyId.value, presetCode ?? instanceName)
			loadingState.presetsRequest = false

			const sortedPresets = sortPresetsByOrder(request)
			presets.value = sortedPresets
			return sortedPresets
		} catch (error) {
			logger.debug.error(error)
			loadingState.presetsRequest = false

			presets.value = []
			return error
		}
	}

	const getMultiplePresets = async (queryParams = {}) => {
		loadingState.presetsRequest = true

		try {
			const request = await dataTransferService.presets.getMultiplePresets(selectedCompanyId.value, queryParams)
			loadingState.presetsRequest = false

			const sortedPresets = sortPresetsByOrder(request)
			presets.value = sortedPresets
			return sortedPresets
		} catch (error) {
			logger.debug.error(error)
			loadingState.presetsRequest = false

			presets.value = []
			return error
		}
	}

	const deletePreset = async (presetId, presetCode = null) => {
		loadingState.presetsDeleting = true

		try {
			await dataTransferService.presets.deletePreset(
				selectedCompanyId.value,
				presetCode ? presetCode : instanceName,
				presetId
			)
			loadingState.presetsDeleting = false

			// if (!withoutNotify) {
			// 	logger.ui.pushSuccessNotification({
			// 		text: 'Пресет успешно удален!',
			// 		timeout: 3000,
			// 	})
			// }

			presets.value = splicePresets(presets.value, [
				presets.value.findIndex(({ id }) => id === presetId),
				1,
			])
			return presets.value
		} catch (error) {
			logger.debug.error(error)
			loadingState.presetsDeleting = false

			logger.ui.pushErrorNotification({
				text: $t('Failed to delete preset, please try again later'),
				timeout: 3000,
			})
			return error
		}
	}

	const editPreset = async ({ presetId, payload }) => {
		loadingState.presetsEditing = true

		try {
			const editedPreset = await dataTransferService.presets.editPreset(
				selectedCompanyId.value,
				instanceName,
				presetId,
				payload
			)
			loadingState.presetsEditing = false

			// logger.ui.pushSuccessNotification({
			// 	text: 'Пресет успешно отредактирован!',
			// 	timeout: 3000,
			// })

			presets.value = splicePresets(presets.value, [
				presets.value.findIndex(({ id }) => id === presetId),
				1,
				editedPreset,
			])
			return editedPreset
		} catch (error) {
			logger.debug.error(error)
			loadingState.presetsEditing = false

			logger.ui.pushErrorNotification({
				text: $t('Failed to edit preset, please try again later'),
				timeout: 3000,
			})
			return error
		}
	}

	const patchPreset = async ({ presetId, payload }, presetCode = null, withoutNotify = false) => {
		loadingState.presetsEditing = true

		try {
			const editedPreset = await dataTransferService.presets.patchPreset(
				selectedCompanyId.value,
				presetCode ? presetCode : instanceName,
				presetId,
				payload
			)
			loadingState.presetsEditing = false
			// console.log(payload)
			// logger.ui.pushSuccessNotification({
			// 	text: 'Пресет успешно отредактирован!',
			// 	timeout: 3000,
			// })

			presets.value = splicePresets(presets.value, [
				presets.value.findIndex(({ id }) => id === presetId),
				1,
				editedPreset,
			])
			return editedPreset
		} catch (error) {
			logger.debug.error(error)
			loadingState.presetsEditing = false

			!withoutNotify && logger.ui.pushErrorNotification({
				text: $t('Failed to edit preset, please try again later'),
				timeout: 3000,
			})
			return error
		}
	}

	const createPreset = async (payload, withoutNotify = false, presetCode = null) => {
		loadingState.presetsCreating = true

		try {
			const createdPreset = await dataTransferService.presets.createPreset(
				selectedCompanyId.value,
				presetCode ?? instanceName,
				payload
			)
			loadingState.presetsCreating = false
			// console.log(createdPreset)
			// !withoutNotify &&
			// 	logger.ui.pushSuccessNotification({
			// 		text: 'Пресет успешно создан!',
			// 		timeout: 3000,
			// 	})

			presets.value.push(createdPreset)
			return createdPreset
		} catch (error) {
			logger.debug.error(error)
			loadingState.presetsCreating = false

			!withoutNotify &&
				logger.ui.pushErrorNotification({
					text: $t('Failed to create preset, please try again later'),
					timeout: 3000,
				})
			return error
		}
	}

	const createOrUpdatePreset = async (code, payload) => {
		const preset = await getPresets(code)
		if (preset.length) {
			const presetId = preset.at(-1).id
			return patchPreset({ presetId: presetId, payload }, code, true)
		} else {
			return createPreset(payload, true, code)
		}
	}

	const cloneToCompanyPreset = async (code, presetId, conflict_policy = CONFLICT_POLICY_TO_CLONING_PRESET.REPLACE_ALL) => {
		await dataTransferService.presets.cloneToCompanyPreset(
			selectedCompanyId.value,
			code ?? instanceName,
			presetId,
			{ conflict_policy }
		)
	}

	const createMapLegendPreset = async (code, payload, withoutNotify = false) => {
		loadingState.presetsCreating = true

		try {
			const createdPreset = await dataTransferService.presets.createPreset(
				selectedCompanyId.value,
				code,
				payload,
			)
			loadingState.presetsCreating = false

			// !withoutNotify &&
			// 	logger.ui.pushSuccessNotification({
			// 		text: 'Пресет успешно создан!',
			// 		timeout: 3000,
			// 	})

			presets.value.push(createdPreset)
			return createdPreset
		} catch (error) {
			logger.debug.error(error)
			loadingState.presetsCreating = false

			!withoutNotify &&
				logger.ui.pushErrorNotification({
					text: $t('Failed to create preset, please try again later'),
					timeout: 3000,
				})
			return error
		}
	}

	const changePresetsOrder = async (payload) => {
		loadingState.presetsChangingOrder = true

		try {
			const request = await dataTransferService.presets.changePresetsOrder(
				selectedCompanyId.value,
				instanceName,
				payload
			)
			loadingState.presetsChangingOrder = false

			// logger.ui.pushSuccessNotification({
			// 	text: 'Порядок пресетов успешно изменен!',
			// 	timeout: 3000,
			// })

			return request
		} catch (error) {
			logger.debug.error(error)
			loadingState.presetsChangingOrder = false

			logger.ui.pushErrorNotification({
				text: $t('Failed to reorder presets, please try again late'),
				timeout: 3000,
			})
		}
	}

	const initBasePresets = async () => {
		loadingState.presetsRequest = true

		try {
			await saveBasePresets(basePresets)
		} catch (error) {
			logger.debug.error(error)
		}

		loadingState.presetsRequest = false
	}

	const restoreBasePresets = async () => {
		loadingState.presetsRestoring = true
		const existBasePresetsUniqIds = presets.value
			.map(({ config }) => config.uniqBaseId)
			.filter((id) => id)
		const excludedBasePresets = basePresets.filter(
			({ config }) => !existBasePresetsUniqIds.includes(config.uniqBaseId)
		)

		try {
			await saveBasePresets(excludedBasePresets)

			// logger.ui.pushSuccessNotification({
			// 	text: 'Базовые пресеты успешно восстановлены!',
			// 	timeout: 3000,
			// })
		} catch (error) {
			logger.debug.error(error)
			logger.ui.pushErrorNotification({
				text: $t('Failed to restore base presets, please try again later'),
				timeout: 3000,
			})
		}

		loadingState.presetsRestoring = false
	}

	const saveBasePresets = async (presets) => {
		const creationPromises = presets.map((preset) => createPreset(preset, true))
		return Promise.all(creationPromises)
	}

	const splicePresets = (presets, spliceArgs) => {
		const presetsCopy = presets.slice()

		presetsCopy.splice(...spliceArgs)

		return presetsCopy
	}

	const selectPreset = (presetId) => {
		const isPresetExist = findPresetById(presetId)

		if (isPresetExist) {
			selectedPresetId.value = presetId
			saveSelectedPresetToURL()
		}
	}

	const findPresetById = (presetId) => {
		return presets.value.find(({ id }) => id == presetId)
	}

	const selectDefaultPreset = () => {
		const urlPresetSelection = urlHelper.getParam(instanceName)
		const isValidUrlSelection = findPresetById(urlPresetSelection)

		if (urlPresetSelection && isValidUrlSelection) {
			selectPreset(Number(urlPresetSelection))
		} else if (presets.value[0]) {
			const { id } = presets.value[0]
			selectPreset(id)
		}
	}

	const saveSelectedPresetToURL = () => {
		urlHelper.addParams({ [instanceName]: selectedPresetId.value })
	}

	const createPayloadForEditingOrCreating = (state, config, renameOnly = false) => {
		const presetValue = !renameOnly
			? Object.fromEntries(
				Object.entries(state)
					.filter(([key]) => config[key])
					.map(([key, valueRef]) => [key, valueRef?.value || valueRef])
			)
			: selectedPreset.value.config.grid

		return copyObject({
			name: config.name,
			config: {
				grid: presetValue,
			},
		})
	}

	// Dev function
	// const deleteAllPresets = () => {
	// 	const requestsFunctions = presets.value.map(
	// 		({ id }) =>
	// 			() =>
	// 				deletePreset(id)
	// 	)

	// 	sendRequestsByPieces(requestsFunctions)
	// }
	// window.deleteAllPresets = deleteAllPresets

	return {
		loadingState,
		presets,
		selectedPresetId,
		selectedPreset,
		selectPreset,
		selectDefaultPreset,
		createPayloadForEditingOrCreating,
		getPresets,
		getMultiplePresets,
		deletePreset,
		editPreset,
		patchPreset,
		createPreset,
		createOrUpdatePreset,
		cloneToCompanyPreset,
		createMapLegendPreset,
		initBasePresets,
		restoreBasePresets,
		changePresetsOrder,
	}
}
